import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

type VarianteKey = "desktop" | "mobile" | "thumb" | "capa";

type VarianteConfig = {
  key: VarianteKey;
  label: string;
  proporcao: string;
  viewportWidth: number;
  viewportHeight: number;
};

type Ajuste = {
  crop_x: number;
  crop_y: number;
  zoom: number;
  proporcao: string;
  viewport_width: number;
  viewport_height: number;
};

type ImagemData = {
  id: number;
  caminho: string;
  caminho_original?: string | null;
  caminho_thumb?: string | null;
  status_imagem?: string | null;
};

const VARIANTES: VarianteConfig[] = [
  {
    key: "desktop",
    label: "Desktop",
    proporcao: "16:9",
    viewportWidth: 1280,
    viewportHeight: 720,
  },
  {
    key: "mobile",
    label: "Mobile",
    proporcao: "4:5",
    viewportWidth: 430,
    viewportHeight: 540,
  },
  {
    key: "thumb",
    label: "Thumb",
    proporcao: "1:1",
    viewportWidth: 400,
    viewportHeight: 400,
  },
  {
    key: "capa",
    label: "Capa",
    proporcao: "3:1",
    viewportWidth: 1500,
    viewportHeight: 500,
  },
];

const ajustePadrao = (cfg: VarianteConfig): Ajuste => ({
  crop_x: 50,
  crop_y: 50,
  zoom: 1,
  proporcao: cfg.proporcao,
  viewport_width: cfg.viewportWidth,
  viewport_height: cfg.viewportHeight,
});

function csrfToken(): string {
  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  );
}

function buildImageUrl(path?: string | null): string {
    if (!path) return "";

    const value = String(path).trim();

    if (!value) return "";

    if (value.startsWith("http://") || value.startsWith("https://")) {
        return value;
    }

    if (value.startsWith("/storage/")) {
        return value;
    }

    if (value.startsWith("storage/")) {
        return `/${value}`;
    }

    if (value.startsWith("/")) {
        return value;
    }

    return `/storage/${value.replace(/^\/+/, "")}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function aspectFromString(proporcao: string): number {
  const parts = proporcao.split(":").map(Number);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return 1;
  return parts[0] / parts[1];
}

function PreviewBox({
  src,
  ajuste,
  title,
  frameWidth,
  showChecker = false,
}: {
  src: string;
  ajuste: Ajuste;
  title: string;
  frameWidth: number;
  showChecker?: boolean;
}) {
  const aspect = aspectFromString(ajuste.proporcao);
  const frameHeight = frameWidth / aspect;

  const imgWidth = frameWidth * ajuste.zoom;
  const imgHeight = frameHeight * ajuste.zoom;

  const tx = ((ajuste.crop_x - 50) / 50) * ((imgWidth - frameWidth) / 2);
  const ty = ((ajuste.crop_y - 50) / 50) * ((imgHeight - frameHeight) / 2);

  return (
    <div className="rounded-2xl border bg-white p-3 shadow-sm">
      <div className="mb-2 text-sm font-semibold text-slate-700">{title}</div>

      <div
        className={`relative overflow-hidden rounded-xl border bg-slate-100 ${
          showChecker
            ? "bg-[linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%,#f1f5f9),linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%,#f1f5f9)] bg-[length:20px_20px] bg-[position:0_0,10px_10px]"
            : ""
        }`}
        style={{
          width: `${frameWidth}px`,
          height: `${frameHeight}px`,
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        {src ? (
          <img
            src={src}
            alt={title}
            draggable={false}
            className="pointer-events-none absolute left-1/2 top-1/2 select-none"
            style={{
              width: `${imgWidth}px`,
              height: `${imgHeight}px`,
              maxWidth: "none",
              transform: `translate(calc(-50% - ${tx}px), calc(-50% - ${ty}px))`,
              objectFit: "cover",
              userSelect: "none",
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            Sem imagem
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServicoImagemEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const imagemIdParam = id || searchParams.get("imagem_id") || "";
  const imagemId = Number(imagemIdParam || 0);
  const returnTo = searchParams.get("return_to") || "/admin/servico-imagens";

  const [imagem, setImagem] = useState<ImagemData | null>(null);
  const [carregandoImagem, setCarregandoImagem] = useState(true);

  const [abaAtiva, setAbaAtiva] = useState<VarianteKey>("desktop");
  const [ajustes, setAjustes] = useState<Record<VarianteKey, Ajuste>>({
    desktop: ajustePadrao(VARIANTES[0]),
    mobile: ajustePadrao(VARIANTES[1]),
    thumb: ajustePadrao(VARIANTES[2]),
    capa: ajustePadrao(VARIANTES[3]),
  });

  const [loadingVariante, setLoadingVariante] = useState(false);
  const [savingVariante, setSavingVariante] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  const dragRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    initialCropX: number;
    initialCropY: number;
  } | null>(null);

  const cfgAtiva = useMemo(
    () => VARIANTES.find((v) => v.key === abaAtiva)!,
    [abaAtiva]
  );

  const ajusteAtual = ajustes[abaAtiva];
  const imageSrc = buildImageUrl(
    imagem?.caminho_original ?? imagem?.caminho ?? imagem?.caminho_thumb ?? ""
    );

  const atualizarAjuste = useCallback(
    (patch: Partial<Ajuste>) => {
      setAjustes((prev) => ({
        ...prev,
        [abaAtiva]: {
          ...prev[abaAtiva],
          ...patch,
        },
      }));
    },
    [abaAtiva]
  );

  const carregarImagem = useCallback(async () => {
    if (!imagemId) return;

    setCarregandoImagem(true);
    setErro("");

    try {
      const resp = await fetch(`/servico-imagens/${imagemId}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!resp.ok) {
        throw new Error(`Falha ao carregar imagem (${resp.status})`);
      }

      const data = await resp.json();
      const payload = data?.imagem || data?.data || data;

      setImagem({
        id: payload.id,
        caminho: payload.caminho,
        caminho_original: payload.caminho_original,
        caminho_thumb: payload.caminho_thumb,
        status_imagem: payload.status_imagem,
      });
    } catch (e: any) {
      setErro(e?.message || "Não foi possível carregar a imagem.");
    } finally {
      setCarregandoImagem(false);
    }
  }, [imagemId]);

  const carregarVariante = useCallback(
    async (variante: VarianteKey) => {
      if (!imagemId) return;

      const cfg = VARIANTES.find((v) => v.key === variante)!;
      setLoadingVariante(true);
      setErro("");
      setMensagem("");

      try {
        const resp = await fetch(`/servico-imagens/${imagemId}/variante/${variante}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });

        if (!resp.ok) {
          throw new Error(`Falha ao carregar variante ${variante}`);
        }

        const json = await resp.json();
        const data = json?.data;

        setAjustes((prev) => ({
          ...prev,
          [variante]: data
            ? {
                crop_x: Number(data.crop_x ?? 50),
                crop_y: Number(data.crop_y ?? 50),
                zoom: Number(data.zoom ?? 1),
                proporcao: data.proporcao || cfg.proporcao,
                viewport_width: Number(data.viewport_width ?? cfg.viewportWidth),
                viewport_height: Number(data.viewport_height ?? cfg.viewportHeight),
              }
            : ajustePadrao(cfg),
        }));
      } catch (e: any) {
        setAjustes((prev) => ({
          ...prev,
          [variante]: ajustePadrao(cfg),
        }));
        setErro(e?.message || "Erro ao carregar a variante.");
      } finally {
        setLoadingVariante(false);
      }
    },
    [imagemId]
  );

  const salvarVariante = useCallback(async () => {
    if (!imagemId) return;

    setSavingVariante(true);
    setErro("");
    setMensagem("");

    try {
      const body = {
        variante: abaAtiva,
        crop_x: Number(ajusteAtual.crop_x.toFixed(2)),
        crop_y: Number(ajusteAtual.crop_y.toFixed(2)),
        zoom: Number(ajusteAtual.zoom.toFixed(2)),
        proporcao: ajusteAtual.proporcao,
        viewport_width: Math.round(ajusteAtual.viewport_width),
        viewport_height: Math.round(ajusteAtual.viewport_height),
      };

      const resp = await fetch(`/servico-imagens/${imagemId}/ajustar-variante`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      });

      const json = await resp.json();

      if (!resp.ok || json?.success === false) {
        throw new Error(json?.message || "Falha ao salvar ajuste.");
      }

      setMensagem(`Variante "${cfgAtiva.label}" salva com sucesso.`);
    } catch (e: any) {
      setErro(e?.message || "Não foi possível salvar a variante.");
    } finally {
      setSavingVariante(false);
    }
  }, [abaAtiva, ajusteAtual, cfgAtiva.label, imagemId]);

  const resetarVariante = useCallback(() => {
    setMensagem("");
    setErro("");
    atualizarAjuste(ajustePadrao(cfgAtiva));
  }, [atualizarAjuste, cfgAtiva]);

  const aplicarPreset = useCallback((cfg: VarianteConfig) => {
    setAjustes((prev) => ({
      ...prev,
      [cfg.key]: {
        ...prev[cfg.key],
        proporcao: cfg.proporcao,
        viewport_width: cfg.viewportWidth,
        viewport_height: cfg.viewportHeight,
      },
    }));
  }, []);

  const iniciarDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      initialCropX: ajusteAtual.crop_x,
      initialCropY: ajusteAtual.crop_y,
    };
  };

  const aoMoverMouse = useCallback(
    (e: MouseEvent) => {
      if (!dragRef.current?.active) return;

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const sensibilidade = 0.12;

      const novoX = clamp(dragRef.current.initialCropX - dx * sensibilidade, 0, 100);
      const novoY = clamp(dragRef.current.initialCropY - dy * sensibilidade, 0, 100);

      setAjustes((prev) => ({
        ...prev,
        [abaAtiva]: {
          ...prev[abaAtiva],
          crop_x: novoX,
          crop_y: novoY,
        },
      }));
    },
    [abaAtiva]
  );

  const pararDrag = useCallback(() => {
    if (dragRef.current) {
      dragRef.current.active = false;
    }
  }, []);

  useEffect(() => {
    if (!imagemId) return;
    carregarImagem();
  }, [imagemId, carregarImagem]);

  useEffect(() => {
    if (!imagemId) return;
    carregarVariante(abaAtiva);
  }, [abaAtiva, imagemId, carregarVariante]);

  useEffect(() => {
    window.addEventListener("mousemove", aoMoverMouse);
    window.addEventListener("mouseup", pararDrag);

    return () => {
      window.removeEventListener("mousemove", aoMoverMouse);
      window.removeEventListener("mouseup", pararDrag);
    };
  }, [aoMoverMouse, pararDrag]);

  useEffect(() => {
    VARIANTES.forEach(aplicarPreset);
  }, [aplicarPreset]);

  if (!imagemId || Number.isNaN(imagemId)) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          ID da imagem inválido.
        </div>
      </div>
    );
  }

  const aspect = aspectFromString(ajusteAtual.proporcao);
  const editorWidth = 760;
  const editorHeight = editorWidth / aspect;

  const imgWidth = editorWidth * ajusteAtual.zoom;
  const imgHeight = editorHeight * ajusteAtual.zoom;
  const tx = ((ajusteAtual.crop_x - 50) / 50) * ((imgWidth - editorWidth) / 2);
  const ty = ((ajusteAtual.crop_y - 50) / 50) * ((imgHeight - editorHeight) / 2);

  if (carregandoImagem) {
    return <div className="p-6 text-sm text-slate-600">Carregando editor...</div>;
  }

  if (!imagem) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {erro || "Imagem não encontrada."}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 md:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate(returnTo)}
              className="mb-2 text-sm font-medium text-slate-500 hover:text-slate-800"
            >
              ← Voltar
            </button>

            <h1 className="text-2xl font-bold text-slate-900">Editor de Imagens</h1>
            <p className="mt-1 text-sm text-slate-600">
              Imagem #{imagem.id} — um ajuste independente para cada contexto.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={resetarVariante}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Resetar aba
            </button>

            <button
              type="button"
              onClick={salvarVariante}
              disabled={savingVariante}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingVariante ? "Salvando..." : "Salvar variante"}
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {VARIANTES.map((v) => {
            const active = abaAtiva === v.key;

            return (
              <button
                key={v.key}
                type="button"
                onClick={() => setAbaAtiva(v.key)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? "bg-slate-900 text-white"
                    : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        {mensagem ? (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {mensagem}
          </div>
        ) : null}

        {erro ? (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {erro}
          </div>
        ) : null}

        {loadingVariante ? (
          <div className="mb-4 rounded-xl border bg-white p-4 text-sm text-slate-600">
            Carregando ajuste da aba {cfgAtiva.label}...
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.25fr_420px]">
          <div className="rounded-3xl border bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-lg font-bold text-slate-900">Área principal de edição</div>
                <div className="text-sm text-slate-500">
                  Arraste a imagem com o mouse para reposicionar.
                </div>
              </div>

              <div className="rounded-xl border bg-slate-50 px-3 py-2 text-xs text-slate-600">
                {cfgAtiva.label} · {ajusteAtual.proporcao} · {ajusteAtual.viewport_width}×
                {ajusteAtual.viewport_height}
              </div>
            </div>

            <div className="overflow-auto rounded-2xl border bg-slate-100 p-4">
              <div
                className="relative mx-auto overflow-hidden rounded-2xl border bg-white shadow-inner"
                style={{
                  width: `${editorWidth}px`,
                  height: `${editorHeight}px`,
                  maxWidth: "100%",
                  cursor: "grab",
                }}
                onMouseDown={iniciarDrag}
              >
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt="Edição"
                    draggable={false}
                    className="pointer-events-none absolute left-1/2 top-1/2 select-none"
                    style={{
                      width: `${imgWidth}px`,
                      height: `${imgHeight}px`,
                      maxWidth: "none",
                      transform: `translate(calc(-50% - ${tx}px), calc(-50% - ${ty}px))`,
                      objectFit: "cover",
                    }}
                  />
                ) : null}

                <div className="pointer-events-none absolute inset-0 border-[3px] border-white/80 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.25)]" />
                <div className="pointer-events-none absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white/70" />
                <div className="pointer-events-none absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white/70" />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <div className="mb-1 text-sm font-medium text-slate-700">Posição horizontal</div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={0.1}
                  value={ajusteAtual.crop_x}
                  onChange={(e) => atualizarAjuste({ crop_x: Number(e.target.value) })}
                  className="w-full"
                />
                <div className="mt-1 text-xs text-slate-500">{ajusteAtual.crop_x.toFixed(1)}</div>
              </label>

              <label className="block">
                <div className="mb-1 text-sm font-medium text-slate-700">Posição vertical</div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={0.1}
                  value={ajusteAtual.crop_y}
                  onChange={(e) => atualizarAjuste({ crop_y: Number(e.target.value) })}
                  className="w-full"
                />
                <div className="mt-1 text-xs text-slate-500">{ajusteAtual.crop_y.toFixed(1)}</div>
              </label>

              <label className="block">
                <div className="mb-1 text-sm font-medium text-slate-700">Zoom</div>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={ajusteAtual.zoom}
                  onChange={(e) => atualizarAjuste({ zoom: Number(e.target.value) })}
                  className="w-full"
                />
                <div className="mt-1 text-xs text-slate-500">{ajusteAtual.zoom.toFixed(2)}x</div>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="mb-1 text-sm font-medium text-slate-700">Viewport largura</div>
                  <input
                    type="number"
                    min={1}
                    max={5000}
                    value={ajusteAtual.viewport_width}
                    onChange={(e) =>
                      atualizarAjuste({
                        viewport_width: Number(e.target.value) || 1,
                      })
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>

                <label className="block">
                  <div className="mb-1 text-sm font-medium text-slate-700">Viewport altura</div>
                  <input
                    type="number"
                    min={1}
                    max={5000}
                    value={ajusteAtual.viewport_height}
                    onChange={(e) =>
                      atualizarAjuste({
                        viewport_height: Number(e.target.value) || 1,
                      })
                    }
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <PreviewBox
              src={imageSrc}
              ajuste={ajustes.desktop}
              title="Preview Desktop"
              frameWidth={340}
            />

            <PreviewBox
              src={imageSrc}
              ajuste={ajustes.mobile}
              title="Preview Mobile"
              frameWidth={240}
            />

            <PreviewBox
              src={imageSrc}
              ajuste={ajustes.thumb}
              title="Preview Thumb"
              frameWidth={180}
              showChecker
            />

            <PreviewBox
              src={imageSrc}
              ajuste={ajustes.capa}
              title="Preview Capa"
              frameWidth={340}
            />

            <div className="rounded-2xl border bg-white p-3 shadow-sm">
              <div className="mb-2 text-sm font-semibold text-slate-700">Imagem original</div>
              <div className="overflow-hidden rounded-xl border bg-slate-100">
                {imageSrc ? (
                  <img src={imageSrc} alt="Original" className="block h-auto w-full" />
                ) : (
                  <div className="p-6 text-sm text-slate-500">Sem imagem</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}